import { Metric } from "@/entities/Finance/Finance.model";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import clsx from "clsx";
import { MetricInfoDisplay } from "../components/MetricInfoDisplay";
import { AIMContextViewAtom } from "../state/context.state";
import { useSetAtom } from "jotai";
import {
    calculateDelta,
    getDeltaFormula,
    getFormula,
    getSliceSum,
} from "../utils/deltas";
import { getPeriodRange } from "../utils/periods";
import { FinanceLabFieldType } from "@/entities/FinanceLab/FinanceLab.model";
import { formatFinanceLabValue } from "@/entities/FinanceLab/FinanceLab.format";
import { CalcResult } from "../components/CalcResult";
import { SourceFile } from "../components/SourceFile";

export type DeltaLineageProps = {
    metric?: Metric;
    metricLabel: string;
    popChange: number;
    currentSlice: [string, number][];
    previousSlice: [string, number][];
    resolutionWindow: string;
    fieldType: FinanceLabFieldType;
    popLabel?: string;
};
export function DeltaLineagePanel({
    metric,
    metricLabel,
    currentSlice,
    previousSlice,
    resolutionWindow,
    fieldType,
    popLabel,
}: DeltaLineageProps) {
    const previousFormula = getFormula(previousSlice, fieldType);
    const currentForumla = getFormula(currentSlice, fieldType);
    const deltaFormula = getDeltaFormula(
        previousSlice.map(([period]) => period),
        currentSlice.map(([period]) => period),
        resolutionWindow,
    );
    const setAIMContextView = useSetAtom(AIMContextViewAtom);
    const delta = calculateDelta(currentSlice, previousSlice);
    const sources = [
        ...new Set(
            metric?.lineagedValues?.flatMap((lv) =>
                lv.values?.map((value) => value.meta?.dataAssetId),
            ),
        ),
    ].filter(Boolean) as string[];

    return (
        <div
            className={clsx(
                "px-6 pb-6",
                "custom-scrollbar absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className="sticky my-4 flex flex-col">
                <RightSidebarButton
                    onClick={() => {
                        setAIMContextView(null);
                    }}
                />
                <h2 className="pageheading ">{metricLabel} - % Change</h2>
                {metric && <MetricInfoDisplay metric={metric} />}
            </div>
            <div className="custom-scrollbar hide-scrollbar overflow-y-scroll">
                <div>
                    <h2 className="heading-l mb-4 mt-6">Calculations</h2>
                    <div className="flex flex-col gap-6">
                        <div>
                            <h3 className="heading-m mb-3">
                                {getPeriodRange(
                                    previousSlice.map(([period]) => period),
                                )}
                            </h3>
                            {previousFormula.formula.length > 1 && (
                                <span className="formula-display flex flex-wrap leading-loose">
                                    {previousFormula.formula.map(
                                        (part, index) => (
                                            <span
                                                key={`previous-${part}-${index}`}
                                                className="py-0.5 pr-2"
                                            >
                                                {part}
                                            </span>
                                        ),
                                    )}
                                </span>
                            )}
                            <CalcResult
                                value={formatFinanceLabValue(
                                    getSliceSum(previousSlice),
                                    fieldType,
                                )}
                                showEqualSign={
                                    previousFormula.formula.length > 1
                                }
                            />
                        </div>
                        <div>
                            <h3 className="heading-m mb-3">
                                {getPeriodRange(
                                    currentSlice.map(([period]) => period),
                                )}
                            </h3>
                            {currentForumla.formula.length > 1 && (
                                <span className="formula-display flex flex-wrap leading-loose">
                                    {currentForumla.formula.map(
                                        (part, index) => (
                                            <span
                                                key={`current-${part}-${index}`}
                                                className="py-0.5 pr-2"
                                            >
                                                {part}
                                            </span>
                                        ),
                                    )}
                                </span>
                            )}
                            <CalcResult
                                showEqualSign={
                                    currentForumla.formula.length > 1
                                }
                                value={formatFinanceLabValue(
                                    getSliceSum(currentSlice),
                                    fieldType,
                                )}
                            />
                        </div>
                        <div>
                            <h3 className="heading-m mb-3">
                                {popLabel || deltaFormula.title}
                            </h3>
                            <span
                                className={clsx(
                                    "flex flex-wrap",
                                    "formula-display items-center gap-y-2",
                                )}
                            >
                                {deltaFormula.formula.map((part, index) =>
                                    ["(", ")", "/", "-"].includes(part) ? (
                                        <span
                                            key={`delta-${part}-${index}`}
                                            className="py-0.5 pr-2"
                                        >
                                            {part}
                                        </span>
                                    ) : (
                                        <span
                                            key={`delta-${part}-${index}`}
                                            className={clsx(
                                                "text-default mr-2 px-2 py-0.5",
                                                "border-default rounded-lg border",
                                            )}
                                            style={{ color: "white" }}
                                        >
                                            {part}
                                        </span>
                                    ),
                                )}

                                <CalcResult
                                    value={
                                        delta
                                            ? `${delta < 0 ? "" : "+"}${(delta * 100).toFixed(2)}%`
                                            : null
                                    }
                                />
                            </span>
                        </div>
                    </div>

                    <h2 className="heading-l mb-4 mt-6">Sources</h2>
                    <div className="flex flex-col gap-3">
                        {sources.map((source) => (
                            <SourceFile
                                fileId={source}
                                key={source}
                                onSelect={() => {
                                    setAIMContextView(null);
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
